import { createSelector } from 'reselect';
import { InAppToBreakdown } from './constants';
/**
 * campaignAnalyticsSelector
 * Selector for the campaignAnalytics state.
 * @param { Object } state: The application state.
 * @returns { Object } The campaignAnalytics state.
 */
export const campaignAnalyticsSelector = (state) => state.campaignAnalytics;

/**
 * selectIsLoading
 * Selects the isLoading data from state.
 * @param { Object } state: The application state.
 * @returns { Any } The isLoading state.
 */
export const selectIsLoading = (state) => campaignAnalyticsSelector(state).isLoading;

export const selectSelectedVariant = (state) => campaignAnalyticsSelector(state).selectedVariant;

export const selectCampaignAnalyticsStatsForTimerange = (state) =>
  campaignAnalyticsSelector(state).statsForTimeRange;

export const selectCampaignLinkStats = (state) =>
  campaignAnalyticsSelector(state).campaignLinkStats;

export const analyticsMetricsOptionsSelector = (state) =>
  campaignAnalyticsSelector(state).analyticsMetricsOptions;

export const selectCampaignMetrics = (state) => campaignAnalyticsSelector(state).campaignMetrics;

export const selectProjectId = (state) => state?.userContext?.project?.id;

export const selectCampaign = (state) => campaignAnalyticsSelector(state).campaign;

export const selectMessageMedium = (state) => selectCampaign(state)?.messageMedium;

export const selectTemplateId = (state) => selectCampaign(state)?.templateId;

export const selectCustomConversion = (state) => selectCampaign(state)?.customConversion;

export const selectCampaignMobileAppIds = (state) => selectCampaign(state)?.mobileAppIds;

export const selectAllDestinations = (state) => campaignAnalyticsSelector(state).allDestinations;

export const selectTopBounceReasonsBreakdownBuckets = createSelector(
  [selectCampaignMetrics],
  (campaignMetrics) => campaignMetrics?.['Top Bounce Reasons Breakdown']?.value?.buckets,
);

export const selectIsCustomConversion = createSelector(
  [selectCustomConversion],
  (customConversion) => {
    return customConversion?.eventName && customConversion.eventName !== 'iterable_purchase';
  },
);

export const selectHasStatsForTimeRangeResults = createSelector(
  [selectCampaignAnalyticsStatsForTimerange],
  (statsForTimeRange) => {
    const results = statsForTimeRange?.results;
    return !!results && Object.keys(results).some((key) => results[key]?.value !== 0);
  },
);

export const selectDestinationsByCampaign = createSelector(
  [selectAllDestinations, selectCampaignMobileAppIds],
  (allDestinations, mobileAppIds) => {
    if (!mobileAppIds?.length) {
      return allDestinations?.filter((app) => app.platform === 'iOS' || app.platform === 'Android');
    } else if (mobileAppIds?.[0] === -1) {
      return allDestinations;
    } else if (mobileAppIds?.[0] === -2) {
      return allDestinations.filter((app) => app.platform === 'Web');
    } else if (mobileAppIds?.length) {
      return allDestinations.filter((app) => mobileAppIds.includes(app.id));
    }
    return [];
  },
);

export const selectDestinationsByBreakdownMetrics = createSelector(
  [selectCampaignAnalyticsStatsForTimerange],
  (statsForTimeRange) => {
    const destinationsObj = {};
    Object.keys(statsForTimeRange?.results || {}).forEach((metric) => {
      if (InAppToBreakdown[metric]) {
        const destinations =
          statsForTimeRange.results[InAppToBreakdown[metric]]?.value?.destinations;
        if (destinations?.length) {
          const destinationsWithoutAppName = destinations.filter(
            (destination) => !destination.appName,
          );
          const othersAggValue = destinationsWithoutAppName.reduce(
            (totalVal, dest) => totalVal + parseFloat(dest.value),
            0,
          );

          if (destinationsWithoutAppName.length) {
            const filteredDestinations = destinations.filter((destination) => destination.appName);
            if (metric.toLowerCase().includes('rate')) {
              const othersArr = destinationsWithoutAppName.map((dest) => ({
                appName: `Others (${dest.appPackageName})`,
                platform: '',
                appPackageName: '',
                value: dest.value,
              }));
              destinationsObj[metric] = [...filteredDestinations, ...othersArr];
            } else {
              destinationsObj[metric] = [
                ...filteredDestinations,
                { appName: 'Others', platform: '', appPackageName: '', value: othersAggValue },
              ];
            }
          } else {
            destinationsObj[metric] = destinations;
          }
        }
      }
    });
    return destinationsObj;
  },
);

export const selectAnalyticsMetricsOptions = createSelector(
  [analyticsMetricsOptionsSelector],
  (analyticsMetricOptions) =>
    (analyticsMetricOptions || []).map((metric) => ({
      categoryId: metric.category?.toLowerCase(),
      label: metric.metricNiceName,
      id: metric.metricName,
      value: metric.metricName,
    })),
);

export const selectAnalyticsMetricsValueTypes = createSelector(
  [analyticsMetricsOptionsSelector],
  (analyticsMetricOptions) =>
    (analyticsMetricOptions || []).reduce((acc, metric) => {
      acc[metric.metricNiceName] = metric.valueType;
      return acc;
    }, {}),
);
